import { Trans, useLingui } from '@lingui/react/macro';
import type { HTMLProps, ReactNode } from 'react';
import { useState } from 'react';

import { Typography } from '@remarkable/ark-web';
import { Controller, useForm } from 'react-hook-form';

import { BillingInfoCountryChanged } from 'src/ampli';
import { tracker } from 'src/analytics/tracker';
import { ComponentLocations } from 'src/analytics/trackingTypes';
import type { BillingInfoForm } from 'src/api/endpoints/storeApi.types';
import {
  useBillingInfoSuspense,
  useStripeSubscriptionSuspense,
  useUpdateBillingInfo,
} from 'src/api/queries';
import {
  Button,
  CountryPicker,
  Form,
  Input,
  Modal,
  NotificationBox,
  StatePicker,
} from 'src/components';
import { InputTaxId } from 'src/components/InputTaxId';
import { getSupportedCountries } from 'src/utils/countryList';

import { useSearch } from '@tanstack/react-router';
import { cn } from '../../../../utils/classNamesHelper';

interface EditBillingInfoFormProps extends HTMLProps<HTMLDivElement> {
  onSuccess?: () => void;
  onCancel?: () => void;
}

const EditBillingInfoForm = ({
  onSuccess,
  onCancel,
  ...props
}: EditBillingInfoFormProps) => {
  const { t } = useLingui();
  const updateBillingInfo = useUpdateBillingInfo();
  const billingInfo = useBillingInfoSuspense();
  const subscription = useStripeSubscriptionSuspense();
  const isSeats = subscription.data?.managementType === 'seats';
  const supportedCountries = getSupportedCountries();
  const isUS = billingInfo.data?.address.country === 'US';
  const form = useForm<BillingInfoForm>({
    defaultValues: billingInfo.data ?? undefined,
  });

  const onSubmit = (data: BillingInfoForm) => {
    data.companyName ||= null;
    data.vatNumber ||= null;
    data.address.country ||= billingInfo.data?.address.country;
    data.address.state ||= billingInfo.data?.address.state;

    updateBillingInfo.mutate(
      {
        ...data,
      },
      {
        onSuccess: () =>
          billingInfo.refetch().then(() => {
            onSuccess?.();
          }),
      }
    );
  };

  return (
    <div {...props} className={cn('text-left', props.className)}>
      <Form onSubmit={onSubmit} {...form} className="flex flex-col gap-24">
        <Input
          data-cy="billing-name"
          name="name"
          label={t`Name`}
          autoComplete="name"
          focusOnIsEditable
        />
        {isSeats ? (
          <Input
            data-cy="billing-email"
            name="email"
            label={t`Email`}
            required={isSeats}
            type="email"
          />
        ) : null}

        <Input
          data-cy="billing-company-name"
          name="companyName"
          label={t`Company name`}
          required={isSeats}
        />

        <InputTaxId name="vatNumber" required={isSeats} />

        <Input
          data-cy="billing-address1"
          label={t`Billing address 1`}
          autoComplete="address-level1"
          name="address.line1"
        />

        <Input
          data-cy="billing-address2"
          label={t`Billing address 2`}
          autoComplete="address-level2"
          name="address.line2"
        />

        <div className="flex flex-col gap-16 sm:flex-row">
          <Input
            className="flex-1"
            data-cy="billing-zip"
            label={t`Zip`}
            autoComplete="zip"
            name="address.zip"
          />

          <Input
            className="flex-1"
            data-cy="billing-city"
            label={t`City`}
            autoComplete="city"
            name="address.city"
          />
        </div>

        <div className="flex flex-col gap-16 sm:flex-row">
          <StatePicker
            disabled={!!billingInfo.data?.address.state}
            className="flex-1"
            name="address.state"
            countryFieldName="address.country"
            data-cy="billing-state"
          />
          <Controller
            control={form.control}
            name="address.country"
            render={({ field }) => (
              <CountryPicker
                disabled={!!billingInfo.data?.address.country}
                className="flex-1"
                data-cy="billing-country"
                countries={supportedCountries}
                onChange={(e) => {
                  const oldValue = field.value || 'unknown';
                  const newValue = e.target.value;

                  tracker.trackEvent(
                    new BillingInfoCountryChanged({
                      from_country: oldValue,
                      to_country: newValue,
                    })
                  );
                }}
                value={field.value}
                name={field.name}
              />
            )}
          />
        </div>
        <NotificationBox
          variant="info"
          title={
            isUS
              ? t`Changing your country or state isn't possible at this time.`
              : t`Changing your country isn't possible at this time.`
          }
          data-cy="billing-country-note"
          muted
        />
        <div className="mt-24 flex justify-center gap-16">
          <Modal.Close asChild>
            <Button
              type="button"
              onClick={() => {
                form.reset();
                updateBillingInfo.reset();
                onCancel?.();
              }}
              variant="secondary"
              data-cy="cancel-edit-billing-info-button"
              analytics={{
                text: 'Cancel',
                location: ComponentLocations.MANAGE_CONNECT.MAIN,
                action: 'cancel edit billing info',
              }}
            >
              <Trans>Cancel</Trans>
            </Button>
          </Modal.Close>

          <Button
            type="submit"
            loading={updateBillingInfo.isPending}
            data-cy="save-billing-info-button"
            disabled={!form.formState.isDirty}
            analytics={{
              text: 'Save',
              location: ComponentLocations.MANAGE_CONNECT.MAIN,
              action: 'save billing info',
            }}
          >
            <Trans>Save</Trans>
          </Button>
        </div>

        {updateBillingInfo.isError && (
          <NotificationBox
            className="mt-24"
            error={updateBillingInfo.error}
            title={t`Unable to update billing info`}
            data-cy="update-billing-info-error"
          >
            <Typography variant="body-md">
              <Trans>Please check if your information is correct</Trans>
            </Typography>
          </NotificationBox>
        )}
      </Form>
    </div>
  );
};

export const BillingInfoModal = ({ trigger }: { trigger: ReactNode }) => {
  const { t } = useLingui();
  const search = useSearch({ strict: false });
  const [open, setOpen] = useState(!!search.openBillingInfo);
  return (
    <Modal.Root open={open} onOpenChange={setOpen}>
      <Modal.Trigger asChild>{trigger}</Modal.Trigger>

      <Modal.Content title={t`Billing information`}>
        <EditBillingInfoForm onSuccess={() => setOpen(false)} />
      </Modal.Content>
    </Modal.Root>
  );
};
